import type { ReactNode } from 'react';
import { useState } from 'react';
import {
  AlertTriangle, BookOpen, Code2, CreditCard, FlaskConical, LandPlot, LayoutDashboard, LogOut, Menu,
  Banknote, Gavel, Receipt, ShieldCheck, Wallet, X, Zap,
} from 'lucide-react';
import { useAuth } from '../lib/client';
import { Wordmark, docsUrl } from '@riskpay/ui-components';

export type Route =
  | 'dashboard'
  | 'transactions'
  | 'settlements'
  | 'payouts'
  | 'settlement-accounts'
  | 'disputes'
  | 'developers'
  | 'onboarding';

const NAV: { id: Route; label: string; icon: typeof LayoutDashboard }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'transactions', label: 'Transactions', icon: Receipt },
  { id: 'settlements', label: 'Settlements', icon: Wallet },
  { id: 'payouts', label: 'Payouts', icon: Banknote },
  { id: 'settlement-accounts', label: 'Payout Accounts', icon: LandPlot },
  { id: 'disputes', label: 'Disputes', icon: Gavel },
  { id: 'developers', label: 'Developers', icon: Code2 },
  { id: 'onboarding', label: 'KYC & Onboarding', icon: ShieldCheck },
];

/**
 * Environment switch.
 *
 * The most consequential control in the portal, and the one people were
 * getting wrong. Three things do the work:
 *
 *   · the two states carry different colours — amber for sandbox, emerald for
 *     real money — so which one you are in is answerable from across a desk;
 *   · each option names what it contains ("Sandbox data" / "Real money"), not
 *     just what it is called;
 *   · switching is confirmed by the banner below the header, so the change is
 *     visible somewhere other than the control you just clicked.
 *
 * It is a segmented control rather than a toggle because a toggle implies a
 * default and an exception. These are two equal environments.
 */
function ModeSwitch() {
  const session = useAuth((s) => s.session);
  const setMode = useAuth((s) => s.setMode);
  const mode = session?.mode ?? 'TEST';

  const options = [
    { value: 'TEST' as const, label: 'Test', hint: 'Sandbox data', Icon: FlaskConical },
    { value: 'LIVE' as const, label: 'Live', hint: 'Real money', Icon: Zap },
  ];

  return (
    <div
      className="flex items-center gap-0.5 rounded-lg border border-line bg-surface-inset p-1"
      role="group"
      aria-label="Environment"
    >
      {options.map(({ value, label, hint, Icon }) => {
        const active = mode === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => setMode(value)}
            aria-pressed={active}
            title={value === 'TEST' ? 'Simulated payments. Nothing is charged.' : 'Real payments and real settlements.'}
            className={`flex items-center gap-2 rounded-md px-3 py-1.5 transition-colors ${
              active
                ? value === 'LIVE'
                  ? 'bg-brand-500 text-[#03150E]'
                  : 'bg-warning-soft text-warning-text shadow-[inset_0_0_0_1px_rgba(240,160,32,0.4)]'
                : 'text-content-subtle hover:text-content'
            }`}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="text-left leading-none">
              <span className="block text-xs font-semibold">{label}</span>
              <span className={`mt-0.5 block text-3xs ${active ? 'opacity-75' : 'opacity-0 sm:opacity-60'}`}>
                {hint}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Environment banner.
 *
 * Test mode says so in words, permanently, immediately under the header. A
 * merchant who integrates against sandbox data and believes it is live is the
 * expensive version of this confusion, and a 12px pill in a corner was never
 * going to prevent it.
 */
function EnvironmentBanner({ mode, kycStatus }: { mode: 'TEST' | 'LIVE'; kycStatus?: string }) {
  const setMode = useAuth((s) => s.setMode);

  if (mode === 'TEST') {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-warning-ring bg-warning-soft px-4 py-2.5 sm:px-6">
        <span className="flex items-center gap-2 text-xs font-medium text-warning-text">
          <FlaskConical className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Test mode
        </span>
        <span className="text-xs text-warning-text/80">
          Everything below is simulated. No money moves, and these payments never reach a bank.
        </span>
        <button
          type="button"
          onClick={() => setMode('LIVE')}
          className="ml-auto text-xs font-medium text-warning-text underline underline-offset-4 hover:opacity-80"
        >
          Switch to live
        </button>
      </div>
    );
  }

  if (kycStatus && kycStatus !== 'APPROVED') {
    return (
      <div className="flex items-start gap-2.5 border-b border-warning-ring bg-warning-soft px-4 py-3 text-xs leading-relaxed text-warning-text sm:px-6">
        <CreditCard className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <p>
          Live mode is read-only until compliance approves your account. Keep building against Test mode — nothing you
          integrate will need to change.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-brand-900 bg-brand-soft px-4 py-2.5 sm:px-6">
      <span className="flex items-center gap-2 text-xs font-medium text-brand-300">
        <Zap className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Live mode
      </span>
      <span className="text-xs text-brand-300/75">Real payments, real settlements. Every action here moves money.</span>
    </div>
  );
}

export function AppShell({
  route,
  onNavigate,
  kycStatus,
  children,
}: {
  route: Route;
  onNavigate: (route: Route) => void;
  kycStatus?: string;
  children: ReactNode;
}) {
  const [navOpen, setNavOpen] = useState(false);
  const session = useAuth((s) => s.session);
  const signOut = useAuth((s) => s.signOut);
  const isLive = session?.mode === 'LIVE';

  return (
    <div className="min-h-screen">
      {isLive && <div className="fixed inset-x-0 top-0 z-[60] h-0.5 bg-brand-500" aria-hidden="true" />}

      {/* ---- Sidebar --------------------------------------------------- */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-line bg-surface-raised transition-transform lg:translate-x-0 ${
          navOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="border-b border-line px-5 py-5">
          <Wordmark subtitle="Merchant Portal" size={46} />
        </div>

        <nav className="flex-1 space-y-0.5 p-3" aria-label="Sections">
          {NAV.map((item) => {
            const Icon = item.icon;
            const isActive = item.id === route;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  onNavigate(item.id);
                  setNavOpen(false);
                }}
                aria-current={isActive ? 'page' : undefined}
                className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-[15px] transition-colors ${
                  isActive
                    ? 'bg-surface-overlay text-content shadow-[inset_2px_0_0_0_#10B981]'
                    : 'text-content-muted hover:bg-surface-overlay/60 hover:text-content'
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {item.label}
                {item.id === 'onboarding' && kycStatus && kycStatus !== 'APPROVED' && (
                  <span className="ml-auto h-1.5 w-1.5 rounded-full bg-warning" aria-label="Action required" />
                )}
              </button>
            );
          })}
        </nav>

        <div className="border-t border-line p-3">
          <a
            href={docsUrl()}
            className="flex items-center gap-3 rounded-md px-3 py-2.5 text-[15px] text-content-muted transition-colors hover:bg-surface-overlay/60 hover:text-content"
          >
            <BookOpen className="h-4 w-4" aria-hidden="true" />
            Documentation
          </a>
          <div className="mt-2 rounded-md border border-line bg-surface-inset px-3 py-2.5">
            <p className="truncate text-xs text-content">{session?.user.email}</p>
            <p className="mt-0.5 text-2xs uppercase tracking-wider text-content-faint">{session?.user.role}</p>
            <button type="button" onClick={signOut} className="btn btn-ghost mt-2 h-7 w-full justify-start px-1.5 text-2xs">
              <LogOut className="h-3 w-3" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {navOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
        />
      )}

      {/* ---- Main ------------------------------------------------------ */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-line bg-surface/90 px-4 backdrop-blur-md sm:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setNavOpen((v) => !v)}
              className="btn btn-secondary h-9 w-9 p-0 lg:hidden"
              aria-label="Open navigation"
            >
              {navOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
            <h1 className="text-md font-semibold tracking-tight">{NAV.find((n) => n.id === route)?.label}</h1>
          </div>

          <div className="flex items-center gap-3">
            {kycStatus && kycStatus !== 'APPROVED' && (
              <span className="badge badge-warning hidden sm:inline-flex">
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                KYC {kycStatus.replace(/_/g, ' ').toLowerCase()}
              </span>
            )}
            <ModeSwitch />
          </div>
        </header>

        <EnvironmentBanner mode={isLive ? 'LIVE' : 'TEST'} kycStatus={kycStatus} />

        <main className="px-4 py-6 sm:px-6 sm:py-8">{children}</main>
      </div>
    </div>
  );
}
